import { toast } from 'react-toastify';

interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number; // in milliseconds
  maxDelay?: number;
  backoffFactor?: number;
  retryCondition?: (error: any) => boolean;
  onRetry?: (attempt: number, error: any) => void;
  showUserFeedback?: boolean;
}

interface _RetryableError extends Error {
  isRetryable?: boolean;
  userFriendlyMessage?: string;
}

// Default retry condition - retry on network errors, timeouts, and 5xx errors
const defaultRetryCondition = (error: any): boolean => {
  // Don't retry user authentication errors (4xx except timeout/rate limit)
  if (error.response?.status >= 400 && error.response?.status < 500) {
    const retryable4xxCodes = [408, 429, 499]; // timeout, rate limit, client closed
    return retryable4xxCodes.includes(error.response.status);
  }
  
  // Retry server errors (5xx)
  if (error.response?.status >= 500) {
    return true;
  }
  
  // Retry network errors
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
    return true;
  }
  
  // Retry timeout errors
  if (error.message?.toLowerCase().includes('timeout')) {
    return true;
  }
  
  // Don't retry by default
  return false;
};

/**
 * Retry a function with exponential backoff
 */
export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> => {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 30000,
    backoffFactor = 2,
    retryCondition = defaultRetryCondition,
    onRetry,
    showUserFeedback = true
  } = options;

  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await fn();
      
      // Success after retries - show recovery message
      if (attempt > 0 && showUserFeedback) {
        toast.success(`✅ Operation succeeded after ${attempt} retry${attempt > 1 ? 'ies' : ''}!`);
      }
      
      return result;
    } catch (error: any) {
      lastError = error;
      
      // If this is the last attempt, throw the error
      if (attempt === maxRetries) {
        if (showUserFeedback) {
          const friendlyMessage = error.userFriendlyMessage || 
            `Operation failed after ${maxRetries} attempts. Please try again later.`;
          toast.error(friendlyMessage);
        }
        throw error;
      }
      
      // Check if we should retry this error
      if (!retryCondition(error)) {
        if (showUserFeedback && error.userFriendlyMessage) {
          toast.error(error.userFriendlyMessage);
        }
        throw error;
      }
      
      // Calculate delay with exponential backoff
      const delay = Math.min(baseDelay * Math.pow(backoffFactor, attempt), maxDelay);
      
      // Notify about retry
      if (onRetry) {
        onRetry(attempt + 1, error);
      }
      
      if (showUserFeedback) {
        const retryMessage = getRetryMessage(attempt + 1, delay, error);
        toast.warning(retryMessage, { autoClose: 3000 });
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

/**
 * Get user-friendly retry message
 */
const getRetryMessage = (attempt: number, delay: number, error: any): string => {
  const seconds = Math.ceil(delay / 1000);
  
  // Customize message based on error type
  if (error.response?.status === 429) {
    return `🕐 Rate limited. Retrying in ${seconds}s... (attempt ${attempt})`;
  }
  
  if (error.response?.status >= 500) {
    return `🔄 Server busy. Retrying in ${seconds}s... (attempt ${attempt})`;
  }
  
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return `⏱️ Request timed out. Retrying in ${seconds}s... (attempt ${attempt})`;
  }
  
  if (error.code === 'ERR_NETWORK') {
    return `🌐 Network error. Retrying in ${seconds}s... (attempt ${attempt})`;
  }
  
  return `🔄 Retrying in ${seconds}s... (attempt ${attempt})`;
};

/**
 * Retry wrapper specifically for video operations
 */
export const retryVideoOperation = async <T>(
  fn: () => Promise<T>,
  operationType: 'upload' | 'analysis' | 'export' | 'processing' = 'processing',
  showUserFeedback: boolean = true
): Promise<T> => {
  const operationConfig = {
    upload: {
      maxRetries: 2, // Conservative for large file uploads
      baseDelay: 2000,
      maxDelay: 10000,
      retryCondition: (error: any) => {
        // Only retry uploads on network errors, not client errors
        return error.code === 'ERR_NETWORK' || 
               error.code === 'ECONNABORTED' ||
               error.response?.status >= 500;
      }
    },
    analysis: {
      maxRetries: 3,
      baseDelay: 5000, // Longer delay for analysis
      maxDelay: 30000,
      retryCondition: (error: any) => {
        // Retry analysis on server errors and timeouts
        return error.response?.status >= 500 ||
               error.response?.status === 429 ||
               error.message?.includes('timeout') ||
               error.message?.includes('overloaded');
      }
    },
    export: {
      maxRetries: 2,
      baseDelay: 3000,
      maxDelay: 15000,
      retryCondition: defaultRetryCondition
    },
    processing: {
      maxRetries: 3,
      baseDelay: 2000,
      maxDelay: 20000,
      retryCondition: defaultRetryCondition
    }
  };

  const config = operationConfig[operationType];
  
  return retryWithBackoff(fn, {
    ...config,
    showUserFeedback, // Pass through the showUserFeedback parameter
    onRetry: (_attempt, _error) => {
      // console.log(`🔄 Retrying ${operationType} operation (attempt ${attempt}):`, error.message);
    }
  });
};

/**
 * Smart retry for API calls with circuit breaker pattern
 */
class SmartRetryManager {
  private failureCount = new Map<string, number>();
  private lastFailureTime = new Map<string, number>();
  private circuitBreakerThreshold = 5;
  private circuitBreakerResetTime = 5 * 60 * 1000; // 5 minutes

  async retryApiCall<T>(
    key: string,
    fn: () => Promise<T>,
    options: RetryOptions = {}
  ): Promise<T> {
    // Check circuit breaker
    if (this.isCircuitOpen(key)) {
      const timeToReset = this.getTimeToReset(key);
      throw new Error(`Circuit breaker open for ${key}. Try again in ${Math.ceil(timeToReset / 1000)}s`);
    }

    try {
      const result = await retryWithBackoff(fn, options);
      
      // Reset failure count on success
      this.failureCount.set(key, 0);
      
      return result;
    } catch (error) {
      // Increment failure count
      const failures = (this.failureCount.get(key) || 0) + 1;
      this.failureCount.set(key, failures);
      this.lastFailureTime.set(key, Date.now());
      
      throw error;
    }
  }

  private isCircuitOpen(key: string): boolean {
    const failures = this.failureCount.get(key) || 0;
    const lastFailure = this.lastFailureTime.get(key) || 0;
    
    if (failures >= this.circuitBreakerThreshold) {
      const timeSinceLastFailure = Date.now() - lastFailure;
      return timeSinceLastFailure < this.circuitBreakerResetTime;
    }
    
    return false;
  }

  private getTimeToReset(key: string): number {
    const lastFailure = this.lastFailureTime.get(key) || 0;
    const timeSinceLastFailure = Date.now() - lastFailure;
    return Math.max(0, this.circuitBreakerResetTime - timeSinceLastFailure);
  }
}

// Export singleton instance
export const smartRetryManager = new SmartRetryManager();

/**
 * Wrapper for API calls with smart retry and circuit breaker
 */
export const withSmartRetry = <T>(
  key: string,
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> => {
  return smartRetryManager.retryApiCall(key, fn, options);
}; 